// src/oura/components/newscan/KnowledgeFold.tsx
//
// "Notes and files for the research" on the Business step, every purpose
// (JOURNEY-SPEC v1.1 §2.5, the old modal's Knowledge staging restored). A
// fold: folded, its head says how many there are ("4 from the form" on a
// First AI check, whose form answers were filed to Knowledge). Open, it lists
// them and offers "Add a note or file", which stages into Knowledge against
// the site (stageKnowledgeFile / stageKnowledgeText, as the old modal did):
// the scan that starts from this draft adopts them, and the draft's research
// reads them.
//
// Before the business exists (a new site), the list is the site's staged rows
// (GET /knowledge/staged), read on open so a reopened draft shows what the scan
// will adopt, and each row has Remove (DELETE /knowledge/staged/{id}, backend
// #4994, FE #4760). Once the business exists, removing stays on its Knowledge tab.

import { FileTextIcon, StickyNoteIcon } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { deleteStagedKnowledge, listKnowledge, listStagedKnowledge, stageKnowledgeFile, stageKnowledgeText, type AeoKnowledgeSource } from "@service/aeo";
import { cn } from "@oura/lib/cn";
import { DrawerFold, PillButton, TYPE } from "@oura/ui";
import { inputClass, textareaClass } from "@oura/onboarding/messageText";
import { KNOWLEDGE_ACCEPT, knowledgeDate, knowledgeState } from "@oura/components/KnowledgeEntry";
import { plural } from "@oura/newscan/copy";
import { useWritesPaused } from "@context/maintenance";
import { Muted } from "./Muted";

const COPY = {
    label: "Notes and files for the research",
    fromForm: (n: number) => `${n} from the form`,
    count: (n: number) => plural(n, "note or file", "notes and files"),
    none: "None yet",
    empty: "Nothing yet. A price list, opening hours or an email from the owner helps the research.",
    add: "Add a note or file",
    addFile: "Add a file",
    addNote: "Add a note",
    noteTitle: "Title",
    noteTitlePlaceholder: (n: number) => `Note ${n}`,
    noteText: "Text",
    notePlaceholder: "Opening hours, a price list, an email the owner sent",
    save: "Add the note",
    done: "Done",
    added: (title: string) => `Added ${title}.`,
    already: (title: string) => `${title} is already in Knowledge.`,
    removeHint: "Remove one from the business's Knowledge tab.",
    remove: "Remove",
    removeLabel: (title: string) => `Remove ${title}`,
    removed: (title: string) => `Removed ${title}.`,
    reading: "Reading",
    loadFailed: "Could not load Knowledge. Try again.",
    notLoaded: "Not loaded",
} as const;

export interface KnowledgeFoldProps {
    /** The business, when it exists: its Knowledge is listed. Null before a new business's first scan. */
    businessId: string | null;
    /** The site the staged rows are keyed on. */
    domain: string;
    /** First AI check: how many items the client's form filed to Knowledge. */
    formItems?: number | null;
    disabled?: boolean;
}

export function KnowledgeFold({ businessId, domain, formItems = null, disabled }: KnowledgeFoldProps) {
    const [sources, setSources] = useState<AeoKnowledgeSource[]>([]);
    const [adding, setAdding] = useState<"menu" | "note" | null>(null);
    const [busy, setBusy] = useState(false);
    const [said, setSaid] = useState("");
    const [problems, setProblems] = useState<string[]>([]);
    const fileRef = useRef<HTMLInputElement>(null);
    const writesPaused = useWritesPaused();

    // Everything below belongs to one business and site: a response that comes
    // back after either changed is dropped, never shown against the new one.
    const entityKey = `${businessId ?? ""}|${domain}`;
    const entityKeyRef = useRef(entityKey);
    entityKeyRef.current = entityKey;

    // Loading and a failed read are their own states: neither is "nothing yet" (Jack, #4286).
    // A new site's staged rows are read too: "None yet" is said only once the read says so.
    const [listState, setListState] = useState<"loading" | "failed" | "done">(businessId || domain ? "loading" : "done");
    /** The staged row being removed. One at a time; an attempt from an older site or click never clears a newer one. */
    const [removing, setRemoving] = useState<string | null>(null);
    const removeAttemptRef = useRef(0);

    useEffect(() => {
        const requestedEntity = entityKey;
        let live = true;

        removeAttemptRef.current += 1;
        setRemoving(null);
        setSources([]);
        setListState(businessId || domain ? "loading" : "done");
        setAdding(null);
        setBusy(false);
        setSaid("");
        setProblems([]);

        if (!businessId && !domain) return;

        (businessId ? listKnowledge(businessId) : listStagedKnowledge(domain))
            .then((page) => {
                if (!live || entityKeyRef.current !== requestedEntity) return;
                setSources(page.sources);
                setListState("done");
            })
            .catch(() => {
                if (live && entityKeyRef.current === requestedEntity) setListState("failed");
            });
        return () => {
            live = false;
        };
    }, [businessId, domain, entityKey]);

    const say = (line: string) => setSaid((was) => (was === line ? `${line}\u200b` : line));

    const addFiles = async (files: File[]) => {
        if (!domain) return;
        const requestedEntity = entityKey;
        setBusy(true);
        setProblems([]);
        try {
            // One request per file: a repeat is answered per file, so the good ones still land.
            for (const file of files) {
                // One file failing does not stop the rest: each is tried, and each failure is said (Jack, #4292).
                try {
                    const r = await stageKnowledgeFile(domain, file);
                    if (entityKeyRef.current !== requestedEntity) return;
                    if (r.ok) {
                        setSources((all) => [...r.sources, ...all]);
                        say(COPY.added(file.name));
                    } else setProblems((p) => [...p, r.kind === "duplicate" ? COPY.already(r.title) : `${r.title}: ${r.message}`]);
                } catch {
                    if (entityKeyRef.current !== requestedEntity) return;
                    setProblems((p) => [...p, `${file.name}: could not be added. Try again.`]);
                }
            }
        } finally {
            if (entityKeyRef.current === requestedEntity) {
                setBusy(false);
                setAdding(null);
            }
        }
    };

    const addNote = async (title: string, text: string): Promise<boolean> => {
        if (!domain) return false;
        const requestedEntity = entityKey;
        setBusy(true);
        setProblems([]);
        try {
            const r = await stageKnowledgeText(domain, { title, text, content_as_of: new Date().toISOString().slice(0, 10) });
            if (entityKeyRef.current !== requestedEntity) return false;
            if (!r.ok) {
                setProblems([r.kind === "duplicate" ? COPY.already(r.title) : `${r.title}: ${r.message}`]);
                return false;
            }
            setSources((all) => [...r.sources, ...all]);
            say(COPY.added(title));
            return true;
        } catch {
            if (entityKeyRef.current === requestedEntity) setProblems(["Could not add the note. Try again."]);
            return false;
        } finally {
            if (entityKeyRef.current === requestedEntity) setBusy(false);
        }
    };

    /** Remove a staged row (no business yet). A refusal is said in the server's words, and the list is read again. */
    const removeStaged = async (source: AeoKnowledgeSource) => {
        if (businessId || !domain) return;
        const requestedEntity = entityKey;
        const attempt = ++removeAttemptRef.current;
        const isCurrent = () => removeAttemptRef.current === attempt && entityKeyRef.current === requestedEntity;
        setRemoving(source.id);
        setProblems([]);
        try {
            const r = await deleteStagedKnowledge(source.id, domain);
            if (!isCurrent()) return;
            if (r.ok) {
                setSources((all) => all.filter((x) => x.id !== source.id));
                say(COPY.removed(source.title));
                return;
            }
            setProblems([`${source.title}: ${r.message}`]);
            // Read again in place (not through Try again, which clears the sentence), so a row
            // the server no longer has (404, removed elsewhere) does not stay on screen. A failed
            // read says Not loaded rather than keeping a list it can no longer vouch for.
            try {
                const page = await listStagedKnowledge(domain);
                if (!isCurrent()) return;
                setSources(page.sources);
                setListState("done");
            } catch {
                if (!isCurrent()) return;
                setSources([]);
                setListState("failed");
            }
        } catch {
            if (isCurrent()) setProblems([`${source.title}: could not be removed. Try again.`]);
        } finally {
            if (isCurrent()) setRemoving(null);
        }
    };

    // Read off what the list holds, so the folded head never promises rows the open fold lacks.
    const fromForm = formItems && formItems > 0 && sources.length === formItems;
    const note = listState === "loading" ? COPY.reading : listState === "failed" ? COPY.notLoaded : sources.length === 0 ? COPY.none : fromForm ? COPY.fromForm(formItems) : COPY.count(sources.length);
    const notes = sources.filter((s) => s.kind === "text").length;

    return (
        <DrawerFold id="newscan.business.knowledge" label={COPY.label} note={note} remember>
            <div className="space-y-3">
                {listState === "loading" && sources.length === 0 ? (
                    <Muted as="p">{COPY.reading}</Muted>
                ) : listState === "failed" ? (
                    <p role="alert" className={cn(TYPE.tc, "m-0 font-medium text-rose-600")}>
                        {COPY.loadFailed}
                    </p>
                ) : sources.length === 0 ? (
                    <Muted as="p">{COPY.empty}</Muted>
                ) : (
                    <ul role="list" className="list-plain m-0 divide-y divide-gray-100 p-0">
                        {sources.map((s) => (
                            <SourceRow
                                key={s.id}
                                source={s}
                                onRemove={businessId ? undefined : () => void removeStaged(s)}
                                removing={removing === s.id}
                                disabled={disabled || busy || removing !== null}
                            />
                        ))}
                    </ul>
                )}

                {problems.map((p) => (
                    <p key={p} role="alert" className={cn(TYPE.tc, "m-0 font-medium text-rose-600")}>
                        {p}
                    </p>
                ))}

                <input
                    ref={fileRef}
                    type="file"
                    multiple
                    accept={KNOWLEDGE_ACCEPT}
                    className="hidden"
                    disabled={writesPaused}
                    onChange={(e) => {
                        const files = Array.from(e.target.files ?? []);
                        // Cleared so the same file can be picked again.
                        e.target.value = "";
                        if (files.length) void addFiles(files);
                    }}
                />

                {adding === "note" ? (
                    <NoteForm nextIndex={notes + 1} busy={busy} onAdd={addNote} onDone={() => setAdding(null)} />
                ) : adding === "menu" ? (
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                        <PillButton variant="text" writes busy={busy} disabled={disabled || !domain} onClick={() => fileRef.current?.click()}>
                            {COPY.addFile}
                        </PillButton>
                        <PillButton variant="text" writes disabled={disabled || busy || !domain} onClick={() => setAdding("note")}>
                            {COPY.addNote}
                        </PillButton>
                    </div>
                ) : (
                    <PillButton variant="text" writes disabled={disabled || !domain} onClick={() => setAdding("menu")}>
                        {COPY.add}
                    </PillButton>
                )}

                {businessId && sources.length > 0 && <Muted as="p">{COPY.removeHint}</Muted>}

                <span aria-live="polite" className="sr-only">
                    {said}
                </span>
            </div>
        </DrawerFold>
    );
}

function SourceRow({
    source,
    onRemove,
    removing = false,
    disabled = false,
}: {
    source: AeoKnowledgeSource;
    /** Staged rows only (no business yet): the business's own rows are removed on its Knowledge tab. */
    onRemove?: () => void;
    removing?: boolean;
    disabled?: boolean;
}) {
    const state = knowledgeState(source);
    const Icon = source.kind === "text" ? StickyNoteIcon : FileTextIcon;
    const meta = [state.kind === "ready" ? null : state.label, knowledgeDate(source.contentAsOf)].filter(Boolean).join(" · ");
    return (
        <li className="flex min-w-0 items-start gap-2.5 py-2.5 first:pt-1">
            <Icon aria-hidden size={14} className="mt-[3px] shrink-0 text-gray-400" />
            <span className="min-w-0 flex-1">
                <span className={cn(TYPE.tb, "block break-words text-gray-900")}>{source.title}</span>
                {source.errorDetail && state.kind === "failed" ? (
                    <span className={cn(TYPE.tc, "block break-words font-medium text-rose-600")}>{source.errorDetail}</span>
                ) : meta ? (
                    <Muted className="block">{meta}</Muted>
                ) : null}
            </span>
            {onRemove && (
                <PillButton variant="text" size="sm" writes busy={removing} disabled={disabled} aria-label={COPY.removeLabel(source.title)} onClick={onRemove}>
                    {COPY.remove}
                </PillButton>
            )}
        </li>
    );
}

/** A pasted note: a title (optional) and the text. Stays open after Add, for the next paste. */
function NoteForm({
    nextIndex,
    busy,
    onAdd,
    onDone,
}: {
    nextIndex: number;
    busy: boolean;
    onAdd: (title: string, text: string) => Promise<boolean>;
    onDone: () => void;
}) {
    const id = useId();
    const [title, setTitle] = useState("");
    const [text, setText] = useState("");
    const [added, setAdded] = useState(0);
    const textRef = useRef<HTMLTextAreaElement>(null);
    useEffect(() => {
        textRef.current?.focus();
    }, []);
    const submit = async () => {
        if (!text.trim() || busy) return;
        const ok = await onAdd(title.trim() || COPY.noteTitlePlaceholder(nextIndex + added), text.trim());
        if (!ok) return;
        setAdded((n) => n + 1);
        setTitle("");
        setText("");
        textRef.current?.focus();
    };
    return (
        <div className="space-y-2.5">
            <label htmlFor={`${id}-title`} className={cn(TYPE.lab, "block")}>
                {COPY.noteTitle}
            </label>
            <input
                id={`${id}-title`}
                className={cn(inputClass(), "sm:max-w-sm")}
                value={title}
                placeholder={COPY.noteTitlePlaceholder(nextIndex + added)}
                autoComplete="off"
                onChange={(e) => setTitle(e.target.value)}
            />
            <label htmlFor={`${id}-text`} className={cn(TYPE.lab, "block")}>
                {COPY.noteText}
            </label>
            <textarea
                ref={textRef}
                id={`${id}-text`}
                rows={4}
                className={cn(textareaClass(), "block resize-y")}
                value={text}
                placeholder={COPY.notePlaceholder}
                onChange={(e) => setText(e.target.value)}
            />
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <PillButton size="sm" writes busy={busy} disabled={!text.trim()} onClick={() => void submit()}>
                    {COPY.save}
                </PillButton>
                <PillButton variant="text" disabled={busy} onClick={onDone}>
                    {COPY.done}
                </PillButton>
            </div>
        </div>
    );
}
